import { type QueryResult, type OperationVariables } from '@apollo/react-common';
import type { ApolloQueryResult } from 'apollo-client';
import {
	type QueryHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { useCallback, useState, useEffect } from 'react';
import { type ConcreteRequest } from 'relay-runtime';

import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { fetchQuery } from './fetchQuery.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type { ApolloInteropMutableHelper } from './ApolloInteropMutableHelper.ts';
import { NetworkStatus } from './NetworkStatus.ts';

export type RefetchSupportedType<TData, TVariables> = (
	vars?: TVariables,
) => Promise<Pick<ApolloQueryResult<TData>, 'data' | 'errors'>>;

/**
 * Options for the fetchMore function, mirroring Apollo v2's FetchMoreOptions.
 * @see https://www.apollographql.com/docs/react/v2/data/pagination/
 */
export type FetchMoreOptions<TData, TVariables> = {
	/**
	 * Variables to use for the fetchMore query. These are merged with the original variables.
	 */
	variables?: Partial<TVariables>;
	/**
	 * Function to merge the previous query result with the fetchMore result.
	 * This is required and is where you implement your pagination merge logic.
	 * Note: Uses ApolloInteropMutableHelper to support mutable updates on Relay's readonly types.
	 */
	updateQuery: (
		previousResult: ApolloInteropMutableHelper<TData>,
		options: { fetchMoreResult?: TData; variables?: TVariables },
	) => TData;
};

export type FetchMoreType<TData, TVariables> = (
	options: FetchMoreOptions<TData, TVariables>,
) => Promise<ApolloQueryResult<TData>>;

export type UseQueryInteropSupportedResult<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'startPolling' | 'stopPolling' | 'client' // TODO: deprecate client usage in hook return values in favor of direct access to interop shims and/or respective hooks
> & {
	refetch: RefetchSupportedType<TData, TVariables>;
	fetchMore: FetchMoreType<TData, TVariables>;
	error?: ApolloErrorInterop;
	/**
	 * The current network status of the query.
	 * @see NetworkStatus enum for possible values
	 */
	networkStatus: NetworkStatus;
};

export type UseQueryInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryHookOptions<TData, TVariables>,
	| 'variables'
	| 'skip'
	| 'onError'
	| 'client'
	| 'fetchPolicy'
	| 'errorPolicy'
	| 'pollInterval'
	| 'notifyOnNetworkStatusChange'
	| 'onError'
	| 'onCompleted'
	| 'context'
>;

export type UseQueryInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [GraphQLTaggedNode, UseQueryInteropSupportedHookOptions<TData, TVariables>];

export function useQueryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	...[query, params]: UseQueryInteropSupportedArgs<TData, TVariables>
): UseQueryInteropSupportedResult<TData, TVariables> {
	if (process.env.BUILD_VARIANT === 'relay') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useQueryInteropRelay<TData, TVariables>(...[query, params]);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return useQuery<TData, TVariables>(
			...[
				toDocumentNode(query),
				{
					...params,
					context: { ...params?.context, requestId: (query as ConcreteRequest).params?.id },
				},
			],
		);
	}
}

function useQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	options?: Parameters<typeof useQuery<TData, TVariables>>[1],
): UseQueryInteropSupportedResult<TData, TVariables> {
	const environment = useRelayEnvironment();

	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables);

	if (!('params' in query)) {
		throw new Error(
			'[Relay Migration] unsupported tagged node provided. Please make sure you generated Relay artifacts for your operation',
		);
	}

	const skip = options?.skip || false;
	const context = options?.context;
	const notifyOnNetworkStatusChange = options?.notifyOnNetworkStatusChange || false;

	// State to manage the query result
	const [data, setData] = useState<TData | undefined>(undefined);
	const [loading, setLoading] = useState(!skip);
	const [error, setError] = useState<any>(undefined);
	const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(
		skip ? NetworkStatus.ready : NetworkStatus.loading,
	);
	const onError = useStableCallback(options?.onError || function noop() {});

	// Use fetchQuery to get data
	useEffect(() => {
		if (skip) {
			setLoading(false);
			setData(undefined);
			setError(undefined);
			setNetworkStatus(NetworkStatus.ready);
			return;
		}

		setLoading(true);
		setError(undefined);
		setNetworkStatus(NetworkStatus.loading);

		const subscription = fetchQuery<{ response: TData; variables: TVariables }>(
			environment,
			query,
			variables,
			{
				networkCacheConfig: { metadata: context },
			},
		).subscribe({
			next: (result) => {
				setData(result);
				setLoading(false);
				setNetworkStatus(NetworkStatus.ready);
			},
			error: (err: any) => {
				setError(err);
				setLoading(false);
				// Note: Apollo v2 keeps networkStatus at ready on errors, only sets the error field
				setNetworkStatus(NetworkStatus.ready);
				onError(err);
			},
		});

		return () => subscription.unsubscribe();
	}, [environment, query, variables, skip, onError, context]);

	// Create a refetch function for Apollo compatibility
	const refetch: RefetchSupportedType<TData, TVariables> = useCallback(
		(newVariables?: TVariables) => {
			const varsToUse = newVariables || variables;
			setLoading(true);
			setError(undefined);
			setNetworkStatus(NetworkStatus.refetch);

			return fetchQuery<{ response: TData; variables: TVariables }>(environment, query, varsToUse, {
				networkCacheConfig: { metadata: context },
			})
				.toPromise()
				.then((result) => {
					setData(result);
					setLoading(false);
					setNetworkStatus(NetworkStatus.ready);
					return {
						data: result as TData,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					setError(err);
					setLoading(false);
					setNetworkStatus(NetworkStatus.error);
					throw err;
				});
		},
		[environment, query, variables, context],
	);

	// Create a fetchMore function for pagination support
	const fetchMore: FetchMoreType<TData, TVariables> = useCallback(
		(fetchMoreOpts: FetchMoreOptions<TData, TVariables>) => {
			// Merge the new variables with existing variables
			const fetchMoreVariables = {
				...variables,
				...fetchMoreOpts.variables,
			} as TVariables;

			// Only set loading/networkStatus if notifyOnNetworkStatusChange is enabled
			// This matches Apollo v2 behavior where loading stays false during fetchMore
			// unless explicitly opting in to network status notifications
			if (notifyOnNetworkStatusChange) {
				setLoading(true);
				setNetworkStatus(NetworkStatus.fetchMore);
			}

			return fetchQuery<{ response: TData; variables: TVariables }>(
				environment,
				query,
				fetchMoreVariables,
				{
					networkCacheConfig: { metadata: context },
				},
			)
				.toPromise()
				.then((fetchMoreResult) => {
					// Use updateQuery to merge the new results with existing data
					// Pass empty object if data is undefined (matches Apollo v2 behavior)
					const mergedData = fetchMoreOpts.updateQuery(data ?? ({} as TData), {
						fetchMoreResult: fetchMoreResult as TData,
						variables: fetchMoreVariables,
					});
					setData(mergedData);
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					return {
						data: mergedData,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					throw err;
				});
		},
		[environment, query, variables, context, data, notifyOnNetworkStatusChange],
	);

	return {
		data,
		loading,
		error,
		refetch,
		fetchMore,
		variables,
		networkStatus,
		startPolling() {
			throw new Error(`[Relay Migration] 'startPolling' is not supported yet`);
		},
		stopPolling() {
			throw new Error(`[Relay Migration] 'stopPolling' is not supported yet`);
		},
		get client(): any {
			throw new Error(
				`[Relay Migration] 'client' will not be supported; use other interop functions to achieve the desired result`,
			);
		},
	};
}
